import { useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuoteDocumentTemplatesStore } from "./quoteDocumentTemplatesStore";

interface TemplateNameFieldProps {
  /** The template being renamed. */
  templateId: string;
  /** The template's current name. */
  name: string;
  /** Called with the saved name once the rename lands. */
  onRenamed: (name: string) => void;
}

/**
 * The template's name in the editor header, renamable in place.
 *
 * The rename saves on its own rather than riding the document's Save button:
 * a name is template identity, not document content, and the pickers elsewhere
 * in settings read it straight from the store.
 * @param templateId - The template being renamed
 * @param name - The template's current name
 * @param onRenamed - Called with the saved name once the rename lands
 * @returns The template name field element
 */
export function TemplateNameField({
  templateId,
  name,
  onRenamed,
}: TemplateNameFieldProps) {
  const updateTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.updateTemplate,
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = () => {
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    const trimmed = draft?.trim() ?? "";
    if (!trimmed || trimmed === name) {
      cancel();
      return;
    }
    setIsSaving(true);
    const failure = await updateTemplate(templateId, { name: trimmed });
    setIsSaving(false);
    if (failure) {
      setError(failure);
      return;
    }
    onRenamed(trimmed);
    cancel();
  };

  if (draft === null) {
    return (
      <span className="group/name flex shrink-0 items-center gap-1 border-l pl-3 text-sm font-medium">
        {name}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Edit template name"
                className="size-6 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/name:opacity-100"
                onClick={() => setDraft(name)}
                size="icon"
                variant="ghost"
              >
                <Pencil className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Rename template</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1 border-l pl-3">
      <Input
        autoFocus
        aria-label="Template name"
        className="h-7 w-56 text-sm"
        disabled={isSaving}
        maxLength={QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            save();
          }
          if (event.key === "Escape") {
            event.stopPropagation();
            cancel();
          }
        }}
        value={draft}
      />
      <Button
        aria-label="Save template name"
        className="size-7"
        disabled={isSaving}
        onClick={save}
        size="icon"
        variant="ghost"
      >
        {isSaving ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Check className="size-4" />
        )}
      </Button>
      <Button
        aria-label="Cancel rename"
        className="size-7"
        disabled={isSaving}
        onClick={cancel}
        size="icon"
        variant="ghost"
      >
        <X className="size-4" />
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </span>
  );
}
